import { useEffect, useState, type FormEvent } from "react";
import { useUiStore } from "../state/uiStore";
import { Button, Field, Icon, TextInput } from "../ui";
import { useSession } from "./session";

// Login/Sign-up-Popup (D47, Identität). Sign-up = Name + E-Mail + Passwort; die E-Mail
// dient zur Bestätigung (Verify-Link). Login = E-Mail + Passwort. Global gehängt in der
// AppShell, gesteuert über uiStore.authOpen (z.B. „Sign In" in der SideRail).

type Mode = "login" | "signup";

export function AuthModal() {
  const open = useUiStore((s) => s.authOpen);
  const close = useUiStore((s) => s.closeAuth);
  const { status, signIn, signUp } = useSession();

  const [mode, setMode] = useState<Mode>("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Angemeldet → Popup schließen.
  useEffect(() => {
    if (open && status === "signed-in") close();
  }, [open, status, close]);

  // Esc schließt.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  if (!open) return null;

  const isSignup = mode === "signup";

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    const res = isSignup ? await signUp(name, email, password) : await signIn(email, password);
    setMsg(res.message);
    setBusy(false);
  }

  function switchMode(next: Mode) {
    setMode(next);
    setMsg(null);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button type="button" aria-label="Schließen" className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />
      <div className="panel relative w-full max-w-sm p-6">
        <button
          type="button"
          onClick={close}
          aria-label="Schließen"
          className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-lg text-muted hover:text-text"
        >
          <Icon name="x" size={18} />
        </button>

        <h2 className="mb-1 text-lg font-semibold">{isSignup ? "Konto erstellen" : "Anmelden"}</h2>
        <p className="mb-4 text-sm text-muted">
          {isSignup ? "Name, E-Mail & Passwort — danach E-Mail bestätigen." : "Mit E-Mail & Passwort anmelden."}
        </p>

        <form onSubmit={onSubmit} className="space-y-3">
          {isSignup && (
            <Field label="Name" htmlFor="auth-name">
              <TextInput
                id="auth-name"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Dein Name"
              />
            </Field>
          )}
          <Field label="E-Mail" htmlFor="auth-email">
            <TextInput
              id="auth-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="du@example.com"
            />
          </Field>
          <Field label="Passwort" htmlFor="auth-password">
            <TextInput
              id="auth-password"
              type="password"
              autoComplete={isSignup ? "new-password" : "current-password"}
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </Field>
          <Button
            type="submit"
            variant="primary"
            disabled={busy || !email.trim() || !password || (isSignup && !name.trim())}
            className="w-full"
          >
            {busy ? "…" : isSignup ? "Registrieren" : "Anmelden"}
          </Button>
        </form>

        {msg && <p className="mt-3 text-xs text-muted">{msg}</p>}

        <div className="mt-4 border-t border-border pt-3 text-center text-xs text-muted">
          {isSignup ? (
            <>
              Schon ein Konto?{" "}
              <button type="button" className="font-medium text-accent hover:underline" onClick={() => switchMode("login")}>
                Anmelden
              </button>
            </>
          ) : (
            <>
              Neu hier?{" "}
              <button type="button" className="font-medium text-accent hover:underline" onClick={() => switchMode("signup")}>
                Konto erstellen
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
